import React, { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function CalibrationPanel({ rows, onMoveStage }) {
  const [guardrail, setGuardrail] = useState({ low: 15, mid: 70, high: 15 });

  const dist = useMemo(() => {
    const ds = { low: 0, mid: 0, high: 0, total: rows?.length || 0 };
    (rows || []).forEach(r => {
      const rt = r.rating || 0;
      if (rt >= 4) ds.high++; else if (rt >= 3) ds.mid++; else ds.low++;
    });
    return ds;
  }, [rows]);

  const toPct = n => Math.round((n / Math.max(1, dist.total)) * 100);

  const chartData = [
    { label: "Low (1–2.9)",  actual: toPct(dist.low),  target: guardrail.low,  color: "#f87171" },
    { label: "Mid (3–3.9)",  actual: toPct(dist.mid),  target: guardrail.mid,  color: "#fbbf24" },
    { label: "High (4–5)",   actual: toPct(dist.high), target: guardrail.high, color: "#00ff90" },
  ];

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Calibration — Distribution</span>
        <span className="text-xs text-white/30">{dist.total} reviews</span>
      </div>

      <div className="p-5 space-y-4">

        {/* Bar chart */}
        <ResponsiveContainer width="100%" height={100}>
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.35)" }} />
            <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} unit="%" domain={[0, 100]} />
            <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v}%`, n]} />
            <Bar dataKey="actual" radius={[4,4,0,0]} name="Actual">
              {chartData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.8} />)}
            </Bar>
            <Bar dataKey="target" radius={[4,4,0,0]} name="Target" fill="rgba(255,255,255,0.1)" />
          </BarChart>
        </ResponsiveContainer>

        {/* Progress rows */}
        <div className="space-y-3">
          {chartData.map(d => {
            const diff = d.actual - d.target;
            return (
              <div key={d.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-white/50">{d.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium" style={{ color: d.color }}>{d.actual}%</span>
                    <span className={`text-xs ${Math.abs(diff) <= 3 ? "text-white/25" : diff > 0 ? "text-red-400/60" : "text-[#00ff90]/60"}`}>
                      {diff > 0 ? `+${diff}` : diff}% vs target
                    </span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-white/5">
                  <div className="h-full rounded-full transition-all" style={{ width: `${d.actual}%`, background: d.color }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Guardrail inputs */}
        <div>
          <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Target Distribution %</p>
          <div className="grid grid-cols-3 gap-2">
            {[["low","Low%"],["mid","Mid%"],["high","High%"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-xs text-white/30 block mb-1">{l}</label>
                <input type="number" className={inputCls} value={guardrail[k]}
                  onChange={e => setGuardrail(p => ({ ...p, [k]: Number(e.target.value || 0) }))} />
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-white/20">Targets are advisory — use 9-Box to justify deviations.</p>

        <button onClick={() => rows?.filter(r => r.stage !== "Calibration").forEach(r => onMoveStage?.(r.id, "Calibration"))}
          className="text-xs px-4 py-2 rounded-lg border border-blue-500/25 bg-blue-500/8 text-blue-400/70 hover:bg-blue-500/15 hover:text-blue-400 transition-colors">
          Move All to Calibration
        </button>
      </div>
    </div>
  );
}